/**
 * Cálculo centralizado do Total Expectável e Diferença para Fechos de Turno.
 * USAR este util em FechoTurnoTab.jsx (form colaborador) E em FechosTurnosTab.jsx
 * (edit admin) para evitar divergências de fórmula entre os 2 fluxos.
 */

const VALORES_RASP = [1, 2, 3, 5, 10, 15];

/**
 * Calcula totalVendidas de raspadinhas a partir de stock_X / recepcao_X /
 * stock_final_X (ou s_final_X, nome usado no backend).
 * Vendidas = stock_inicial + recepcao - stock_final
 *
 * 🐛 FIX 21-06-2026: parseFloat em todos os termos evita concatenação de
 * string quando os valores vêm do Mongo como string (e.g. "86.50") em vez de
 * número. Antes deste fix, o total expectável aparecia como "52486.50" para
 * vendas reais de 86.50 — bug reportado no ecrã de Fecho dos Jogos.
 */
export function calcularTotalVendidasRasp(data) {
  return VALORES_RASP.reduce((total, valor) => {
    const stock = parseFloat(data[`stock_${valor}`]) || 0;
    const recepcao = parseFloat(data[`recepcao_${valor}`]) || 0;
    // Aceitar `stock_final_X` (legacy) e `s_final_X` (usado no backend actual)
    const stockFinal =
      parseFloat(data[`stock_final_${valor}`]) ||
      parseFloat(data[`s_final_${valor}`]) ||
      0;
    const vendidas = Math.max(0, stock + recepcao - stockFinal);
    return total + vendidas * valor;
  }, 0);
}

/**
 * Subtrai os totais da manhã se for turno Tarde e tivermos dados.
 * Devolve { vendas, premios } APENAS do turno actual.
 */
export function vendasPremiosDoTurno(data, dadosManha) {
  let vendas = parseFloat(data.premios_master_1_15) || 0;
  let premios = parseFloat(data.premios_total) || 0;
  const isTarde = (data.turno || "").toLowerCase().includes("tarde");
  if (isTarde && dadosManha) {
    vendas = vendas - (parseFloat(dadosManha.premios_master_1_15) || 0);
    premios = premios - (parseFloat(dadosManha.premios_total) || 0);
  }
  return { vendas, premios };
}

/**
 * Converte comissão de revendas (5%) em dinheiro entrado na loja (95%).
 */
export function dinheiroRevendasFromComissao(comissaoRevendas) {
  const c = parseFloat(comissaoRevendas) || 0;
  return c > 0 ? (c / 0.05) * 0.95 : 0;
}

/**
 * Fórmula CENTRAL — única fonte de verdade.
 * Total expectável = Caixa inicial + Raspadinhas vendidas + Vendas - Prémios + Dinheiro revendas
 */
export function calcularTotalExpectavel({ caixaInicial, valorVendaRaspadinhas, vendas, premios, dinheiroRevendas }) {
  return (
    (parseFloat(caixaInicial) || 0)
    + (parseFloat(valorVendaRaspadinhas) || 0)
    + (parseFloat(vendas) || 0)
    - (parseFloat(premios) || 0)
    + (parseFloat(dinheiroRevendas) || 0)
  );
}

/**
 * Calcula totalExpectavel a partir do fecho/editData inteiro. Evita
 * divergências de fórmula entre form e edit.
 */
export function calcularFechoTotais(data, dadosManha = null) {
  const valorVendaRaspadinhas = calcularTotalVendidasRasp(data);
  const { vendas, premios } = vendasPremiosDoTurno(data, dadosManha);
  const dinheiroRevendas = dinheiroRevendasFromComissao(data.comissao_revendas);
  const caixaInicial = parseFloat(data.caixa_inicial_1) || 0;
  const totalExpectavel = calcularTotalExpectavel({
    caixaInicial, valorVendaRaspadinhas, vendas, premios, dinheiroRevendas,
  });
  const caixaFinal = parseFloat(data.caixa_final) || parseFloat(data.caixa_inicial_2) || 0;
  const diferenca = caixaFinal - totalExpectavel;
  return {
    valorVendaRaspadinhas,
    vendas,
    premios,
    dinheiroRevendas,
    caixaInicial,
    caixaFinal,
    totalExpectavel,
    diferenca,
  };
}
